import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";

import { ResourceError as ResourceErrorBase } from "@secondcloset/web-components";

import { message, PaginationProps, Typography } from "antd";
import { debounce } from "lodash-es";

import * as S from "./styles";

import { fetchUsers } from "../../api/accounts/user";
import { assignResourceError, ResourceErrorIndex } from "../../api/fulfillment/resourceError";

interface ResourceErrorProps {
  title?: string;
  resourceError: ResourceErrorIndex;
  isLoading: boolean;
  refetch: () => void;
  pagination: PaginationProps;
  organizationID?: string;
}

export const ResourceError: React.FC<ResourceErrorProps> = ({
  title,
  resourceError,
  isLoading,
  refetch,
  pagination,
  organizationID,
}) => {
  const [searchAssignee, setSearchAssignee] = useState("");
  const orgUsers = useQuery(
    [`users`, { organizationID, searchAssignee }],
    () => fetchUsers({ organization_id: organizationID, q: searchAssignee.trim() }),
    {
      enabled: !!organizationID,
      select: (response) =>
        response.map((a) => ({
          user_id: a.id,
          name: `${a.first_name} ${a.last_name}`,
        })),
    }
  );
  const { mutateAsync: doAssign } = useMutation(assignResourceError);

  const handleSelectAssignee = async (errorID: string, userID?: string) => {
    return await doAssign(
      {
        resource_id: errorID,
        body: { assigned_user_id: userID || "" },
      },
      {
        onSuccess: () => {
          const user = orgUsers.data?.find((u) => u.user_id === userID)?.name;
          message.success(`Successfully ${user ? `assigned to ${user}` : "unassigned"}`);
          refetch();
        },
        onSettled: () => {
          setSearchAssignee("");
        },
      }
    );
  };

  return !!resourceError.meta?.total_entries ? (
    <S.Container>
      <Typography.Title level={4}>
        {title ?? "Resource Error"} ({resourceError.meta?.active_error_count})
      </Typography.Title>
      <ResourceErrorBase
        activeErrorCount={resourceError.meta?.active_error_count}
        data={resourceError.data ?? []}
        assigneeOptions={orgUsers.data}
        onSelectAssignee={handleSelectAssignee}
        onSearchAssignee={debounce(setSearchAssignee, 300)}
        isLoading={isLoading}
        pagination={pagination}
      />
    </S.Container>
  ) : null;
};
